import { FormEvent, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import {
  deleteDirectorLogo,
  getDirectorProfile,
  updateDirectorProfile,
  uploadDirectorLogo,
} from '@/lib/api/director-profile';
import type { DirectorProfile } from '@swiss-magic/shared-types';

export function DirectorProfilePage() {
  const { t } = useTranslation('director');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await getDirectorProfile();
        applyProfile(p);
      } catch (err) {
        if (err instanceof ApiError) showErrorToast(err.userMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function applyProfile(p: DirectorProfile) {
    setName(p.name);
    setDescription(p.description ?? '');
    setLogoUrl(p.logoUrl);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t('director_profile.name_required');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const p = await updateDirectorProfile({
        name: name.trim(),
        description: description.trim() || null,
      });
      applyProfile(p);
      showSuccessToast(t('director_profile.save_success_toast'));
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('director_profile.save_failed_toast'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogoChange(file: File | null) {
    if (!file) return;
    setSubmitting(true);
    try {
      const p = await uploadDirectorLogo(file);
      applyProfile(p);
      showSuccessToast(t('director_profile.logo_updated_toast'));
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : t('director_profile.logo_upload_failed_toast'),
      );
    } finally {
      setSubmitting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function handleLogoDelete() {
    setSubmitting(true);
    try {
      const p = await deleteDirectorLogo();
      applyProfile(p);
      showSuccessToast(t('director_profile.logo_removed_toast'));
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : t('director_profile.logo_remove_failed_toast'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t('director_profile.loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="director-profile-page" className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-2xl px-4 py-8 md:px-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('director_profile.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="flex flex-col gap-5">
              <div className="flex flex-col gap-3">
                <Label>{t('director_profile.logo_label')}</Label>
                {logoUrl ? (
                  <img
                    data-testid="director-logo"
                    src={logoUrl}
                    alt={t('director_profile.logo_alt')}
                    className="max-h-40 max-w-full rounded border bg-muted/30 object-contain"
                  />
                ) : (
                  <div
                    data-testid="director-logo-placeholder"
                    className="flex h-24 max-w-xs items-center justify-center rounded border text-sm text-muted-foreground"
                  >
                    {t('director_profile.logo_placeholder')}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="director-logo-input"
                    className="hidden"
                    onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="director-logo-upload"
                    onClick={() => fileInput.current?.click()}
                    disabled={submitting}
                  >
                    {logoUrl
                      ? t('director_profile.logo_replace')
                      : t('director_profile.logo_upload')}
                  </Button>
                  {logoUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="director-logo-remove"
                      onClick={handleLogoDelete}
                      disabled={submitting}
                    >
                      {t('director_profile.logo_remove')}
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="director-name">{t('director_profile.name_label')}</Label>
                <Input
                  id="director-name"
                  data-testid="director-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && (
                  <p data-testid="director-name-error" className="text-sm text-destructive">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="director-description">{t('director_profile.description_label')}</Label>
                <Textarea
                  id="director-description"
                  data-testid="director-description-input"
                  rows={4}
                  placeholder={t('director_profile.description_placeholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Button
                type="submit"
                data-testid="director-save-button"
                disabled={submitting}
              >
                {submitting
                  ? t('director_profile.saving')
                  : t('director_profile.save_button')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
